import log from 'loglevel'
import {
  Form,
  type RaRecord,
  TextInput,
  useTranslate,
  useNotify,
  NumberInput,
  SelectInput,
  required,
  useGetIdentity,
  Loading
  // useList,
  // ListContextProvider,
  // Datagrid,
  // TextField,
  // DateField,
  // Pagination
} from 'react-admin'
import {
  Box,
  Button,
  // Card,
  // CardContent,
  Chip,
  // Dialog,
  // DialogActions,
  // DialogContent,
  // DialogTitle,
  Paper,
  Typography
} from '@mui/material'
import React from 'react'
import {
  compact,
  filter,
  find,
  flow,
  includes,
  isEmpty,
  isNil,
  isString,
  keys,
  map,
  pick,
  replace,
  size,
  slice,
  split
} from 'lodash/fp'
import { MultipleSpotSelect } from 'components/SpotSelect'
import { type ConfigProps } from 'components/Dashboard'
import {
  ElectricityChartDailyKind,
  ElectricityChartMonthlyKind,
  ElectricityChartYearlyKind
} from '../../../../cdk/bapp/lib/lambda-api/src/types'
import { useFormContext, useFormState, useWatch } from 'react-hook-form'
import {
  format,
  getDay,
  isAfter,
  isBefore,
  parseISO,
  startOfMonth,
  startOfYear,
  subDays
} from 'date-fns/fp'
import { getSpotData, getSpotPdf } from 'components/DataProvider/functions'
import { type PartnerUser } from '@mzc-watchover/adminapp-appsync'
import { DateInput } from 'components/Util'
import { MissingsDialog } from 'components/Download'

log.setLevel((process.env['LOG_LEVEL'] as log.LogLevelDesc) ?? log.levels.DEBUG)

const SpotDataTable: React.FC<{
  selected: RaRecord[]
  partner: string
  config: Record<string, any>
  allBy: { key: string; value: string } | undefined
}> = ({ selected, partner, config, allBy }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const [actionEnable, setActionEnable] = React.useState(false)
  const [missings, setMissings] = React.useState([])
  const [params, setParams] = React.useState<
    { input: any; getDataFunc: any } | undefined
  >(undefined)
  const [open, setOpen] = React.useState<boolean>(false)
  const { getValues, setValue } = useFormContext()
  const { isValid } = useFormState()
  const updateValues = useWatch()
  React.useEffect(() => {
    const { daily } = getValues()
    if (!isNil(daily.date)) return
    const day = getDay(Date.now())
    const startDay =
      process.env['ENV'] === 'prd'
        ? format('yyyy-MM-dd')(subDays(day + 1)(Date.now()))
        : '2024-12-12'
    setValue('daily', {
      date: startDay,
      compareTo: ElectricityChartDailyKind.lastWeek,
      threshold: 1.5
    })
    setValue('monthly', {
      date: !isNil(startDay)
        ? format('yyyy-MM-dd')(startOfMonth(parseISO(startDay)))
        : null,
      compareTo: ElectricityChartMonthlyKind.lastMonth,
      threshold: 97.7
    })
    setValue('yearly', {
      date: !isNil(startDay)
        ? format('yyyy-MM-dd')(startOfYear(parseISO(startDay)))
        : null,
      compareTo: ElectricityChartYearlyKind.lastYear
    })
  })
  React.useEffect(() => {
    if (!isValid) {
      setActionEnable(false)
      return
    }
    const {
      base,
      daily: dailyValues,
      monthly: monthlyValues,
      yearly: yearlyValues
    } = getValues()
    const values = compact(
      map((item: any) =>
        find(
          (key: string) =>
            isNil(item[key]) || (isString(item[key]) && isEmpty(item[key]))
        )(keys(item))
      )([dailyValues, monthlyValues, yearlyValues])
    )
    setActionEnable(
      !isEmpty(selected) && isEmpty(values) && !isNil(base) && !isEmpty(base)
    )
  }, [selected, updateValues, isValid])
  const dailyTargets = [
    { id: ElectricityChartDailyKind.lastWeek, name: '前週比' },
    {
      id: ElectricityChartDailyKind.lastYearWithMonthlyAverage,
      name: '前年同月平均比'
    },
    {
      id: ElectricityChartDailyKind.lastYearWithYearlyAverage,
      name: '前年年間平均比'
    }
  ]
  const monthlyTargets = [
    { id: ElectricityChartMonthlyKind.lastYear, name: '前年比' },
    { id: ElectricityChartMonthlyKind.lastMonth, name: '前月比' },
    {
      id: ElectricityChartMonthlyKind.lastYearWithMonthlyAverage,
      name: '前年同月平均比'
    }
  ]
  const yearlyTargets = [
    { id: ElectricityChartYearlyKind.lastYear, name: '前年比' }
  ]
  const { base } = getValues()
  const createParams = (): {
    input: {
      base: string
      targets: Array<{
        user: Partial<PartnerUser>
        daily: {
          date: string
          compareTo: ElectricityChartDailyKind
          threshold: number
        }
        monthly: {
          date: string
          compareTo: ElectricityChartMonthlyKind
          threshold: number
        }
        yearly: { date: string; compareTo: ElectricityChartYearlyKind }
      }>
      allBy: { key: string; value: string } | undefined
    }
    missings: Array<{
      user: Partial<PartnerUser> & { from: string }
      daily: boolean
      monthly: boolean
      yearly: boolean
    }>
  } => {
    const { startMonthOfFiscalYear, pdfMode } = config
    const { daily, monthly, yearly: yearlyInput } = getValues()
    const yearly = {
      ...yearlyInput,
      date: `${parseISO(yearlyInput.date).getFullYear()}-${
        startMonthOfFiscalYear as string
      }-01`
    }
    const missings = flow([
      map((user: PartnerUser & Record<string, string>) => {
        const from = parseISO(user['from'] as string)
        return {
          user,
          daily: isAfter(parseISO(daily.date))(from),
          monthtly: isAfter(parseISO(monthly.date))(from),
          yearly: isAfter(parseISO(yearly.date))(from)
        }
      }),
      filter(
        (item: {
          user: Partial<PartnerUser> & { from: string }
          daily: boolean
          monthly: boolean
          yearly: boolean
        }) => item.daily || item.monthly || item.yearly
      ),
      map(
        (item: { user: Partial<PartnerUser> & { from: string } }) => item.user
      )
    ])(selected as Array<PartnerUser & Record<string, string>>)
    setMissings(missings)
    const {
      generate: { idKey }
    } = config
    const targets = compact(
      map((user: PartnerUser & Record<string, string>) => {
        if (
          !isNil(
            find(
              (item: Partial<PartnerUser> & { from: string }) =>
                user.id === item.id
            )(missings)
          )
        ) {
          return undefined
        }
        return {
          user: {
            ...pick(['id', 'name', 'spin', 'from'])(user),
            attributes: compact(
              map((key: string) =>
                includes(key)(['id', 'name', 'spin'])
                  ? undefined
                  : { key, value: isNil(user[key]) ? '' : user[key] }
              )(keys(user))
            )
          },
          daily,
          monthly,
          yearly,
          idKey
        }
      })(selected as Array<PartnerUser & Record<string, string>>)
    )
    const input = {
      base,
      targets,
      mode: pdfMode,
      allBy
    }
    return { input, missings }
  }
  const doDownload = (params: {
    input: {
      base: string
      targets: Array<{
        user: Partial<PartnerUser>
        daily: {
          date: string
          compareTo: ElectricityChartDailyKind
          threshold: number
        }
        monthly: {
          date: string
          compareTo: ElectricityChartMonthlyKind
          threshold: number
        }
        yearly: { date: string; compareTo: ElectricityChartYearlyKind }
      }>
      allBy: { key: string; value: string } | undefined
    }
    getDataFunc: (
      partner: string,
      input: {
        base: string
        targets: Array<{
          user: Partial<PartnerUser>
          daily: {
            date: string
            compareTo: ElectricityChartDailyKind
            threshold: number
          }
          monthly: {
            date: string
            compareTo: ElectricityChartMonthlyKind
            threshold: number
          }
          yearly: { date: string; compareTo: ElectricityChartYearlyKind }
        }>
        allBy: { key: string; value: string } | undefined
      }
    ) => Promise<{ data: string | undefined }>
  }): void => {
    const { input, getDataFunc } = params
    notify('downloading', { autoHideDuration: null })
    getDataFunc(partner, input)
      .then(({ data }) => {
        if (!isNil(data)) {
          const binaryString = window.atob(data)
          const len = binaryString.length
          const bytes = new Uint8Array(len)
          for (let i = 0; i < len; ++i) {
            bytes[i] = binaryString.charCodeAt(i)
          }
          const blob = new Blob([bytes], {
            type: 'application/zip'
          })
          const url = URL.createObjectURL(blob)
          const download = document.createElement('a')
          download.href = url
          download.download = `${base as string}.zip`
          download.click()
          window.URL.revokeObjectURL(url)
        }
      })
      .catch(log.error)
      .finally(() => {
        notify('downloading', { autoHideDuration: 0 })
      })
  }
  const onClick = (
    getDataFunc: (
      partner: string,
      input: {
        base: string
        targets: Array<{
          user: Partial<PartnerUser>
          daily: {
            date: string
            compareTo: ElectricityChartDailyKind
            threshold: number
          }
          monthly: {
            date: string
            compareTo: ElectricityChartMonthlyKind
            threshold: number
          }
          yearly: { date: string; compareTo: ElectricityChartYearlyKind }
        }>
        allBy: { key: string; value: string } | undefined
      }
    ) => Promise<{ data: string | undefined }>
  ): void => {
    const { input, missings } = createParams()
    if (!isEmpty(missings)) {
      setParams({ input, getDataFunc })
      setOpen(true)
    } else {
      doDownload({ input, getDataFunc })
    }
  }
  const onClickDownalod = (): void => {
    onClick(getSpotData)
  }
  const onClickPdfDownload = (): void => {
    onClick(getSpotPdf)
  }
  // const MissingsList: React.FC = () => {
  //   const data = map(
  //     (item: { user: Partial<PartnerUser> & { from: string } }) => {
  //       const { name, from, id } = item.user
  //       return {
  //         name,
  //         from: format('yyyy年M月d日')(parseISO(from)),
  //         id: id as string
  //       }
  //     }
  //   )(missings)
  //   const listContext = useList({ data, perPage: 10 })
  //   return (
  //     <ListContextProvider value={listContext}>
  //       <Datagrid bulkActionButtons={false}>
  //         <TextField
  //           source="name"
  //           label="resources.spotData.missingsDialog.list.name"
  //         />
  //         <TextField
  //           source="from"
  //           label="resources.spotData.missingsDialog.list.from"
  //         />
  //       </Datagrid>
  //       <Pagination />
  //     </ListContextProvider>
  //   )
  // }
  // const missingsDialog = React.useMemo(() => {
  //   const handleClose = (): void => {
  //     setOpen(false)
  //   }
  //   const onClick = (): void => {
  //     setOpen(false)
  //   }
  //   const onClickExec = (): void => {
  //     setOpen(false)
  //     if (!isNil(params)) doDownload(params)
  //   }
  //   return (
  //     <>
  //       <Dialog onClose={handleClose} open={open} maxWidth="lg" fullWidth>
  //         <DialogTitle id="missingsDialog">
  //           {translate(`resources.spotData.missingsDialog.title`)}
  //         </DialogTitle>
  //         <DialogContent>
  //           <Card>
  //             <CardContent>
  //               <Typography variant="body1" sx={{ mt: 2 }}>
  //                 {translate('resources.spotData.missingsDialog.content')}
  //               </Typography>
  //               <MissingsList />
  //             </CardContent>
  //           </Card>
  //         </DialogContent>
  //         <DialogActions>
  //           <Button
  //             variant="contained"
  //             onClick={onClickExec}
  //             disabled={isNil(params) || isEmpty(params.input.targets)}>
  //             {translate('resources.spotData.buttons.exec')}
  //           </Button>
  //           <Button variant="outlined" onClick={onClick}>
  //             {translate('resources.spotData.buttons.close')}
  //           </Button>
  //         </DialogActions>
  //       </Dialog>
  //     </>
  //   )
  // }, [open, missings])
  const {
    SpotData: { range, daily, monthly },
    Limits: configLimits
  } = config
  const { start } = configLimits ?? { start: undefined }
  return (
    <>
      <Typography variant="body1">
        {translate('resources.spotData2.fields.daily.title')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          flexDirection: 'row',
          mt: 0.5,
          gap: 1
        }}>
        <DateInput
          source="daily.date"
          label={translate('resources.spotData2.fields.daily.date')}
          disableFuture
          maxDate={
            isAfter(parseISO(`${format('yyyy-MM-dd')(Date.now())}T15:00`))(
              Date.now()
            )
              ? subDays(1)(Date.now())
              : subDays(2)(Date.now())
          }
          views={['year', 'month', 'day']}
          minDate={
            !isNil(start) && isBefore(parseISO(start))(parseISO(range))
              ? parseISO(start)
              : parseISO(range)
          }
        />
        <SelectInput
          source="daily.compareTo"
          label={translate('resources.spotData2.fields.daily.compareTo')}
          choices={dailyTargets}
          validate={required()}
        />
        <NumberInput
          source="daily.threshold"
          label="resources.spotData2.fields.daily.threshold"
          defaultValue={isNil(daily) ? null : daily.threshold}
          step={0.1}
        />
      </Box>
      <Typography variant="body1">
        {translate('resources.spotData2.fields.monthly.title')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          flexDirection: 'row',
          mt: 0.5,
          gap: 1
        }}>
        <DateInput
          source="monthly.date"
          label={translate('resources.spotData2.fields.monthly.date')}
          views={['year', 'month']}
          disableFuture
          minDate={
            !isNil(start) && isBefore(parseISO(start))(parseISO(range))
              ? parseISO(start)
              : parseISO(range)
          }
          maxDate={new Date()}
        />
        <SelectInput
          source="monthly.compareTo"
          label={translate('resources.spotData2.fields.monthly.compareTo')}
          choices={monthlyTargets}
          validate={required()}
        />
        <NumberInput
          source="monthly.threshold"
          label="resources.spotData2.fields.monthly.threshold"
          defaultValue={isNil(monthly) ? null : monthly.threshold}
          step={0.1}
        />
      </Box>
      <Typography variant="body1">
        {translate('resources.spotData2.fields.yearly.title')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          flexDirection: 'row',
          mt: 0.5,
          gap: 1
        }}>
        <DateInput
          source="yearly.date"
          label={translate('resources.spotData2.fields.yearly.date')}
          views={['year']}
          disableFuture
          minDate={
            !isNil(start) && isBefore(parseISO(start))(parseISO(range))
              ? startOfYear(parseISO(start))
              : parseISO(range)
          }
          maxDate={new Date()}
        />
        <SelectInput
          source="yearly.compareTo"
          label={translate('resources.spotData2.fields.yearly.compareTo')}
          choices={yearlyTargets}
          validate={required()}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          flexDirection: 'row',
          gap: 1
        }}>
        <Button
          variant="contained"
          color="primary"
          onClick={onClickPdfDownload}
          disabled={!actionEnable}>
          {translate('resources.spotData.buttons.pdf')}
        </Button>
        <Button
          variant="contained"
          onClick={onClickDownalod}
          disabled={!actionEnable}>
          {translate('resources.spotData.buttons.data')}
        </Button>
        <Box sx={{ ml: 5 }}>
          {
            // @ts-expect-error ts(2339)
            map.convert({ cap: false })((text: string, index: number) => (
              <Typography key={`caution-${index}`}>{text}</Typography>
            ))(split('\n')(translate('resources.spotData.caution')))
          }
        </Box>
      </Box>
      <MissingsDialog
        open={open}
        setOpen={setOpen}
        missings={missings}
        disabled={isNil(params) || isEmpty(params.input.targets)}
        doDownload={() => {
          if (!isNil(params)) doDownload(params)
        }}
      />
    </>
  )
}
export const SpotData2: React.FC<{ partner: string } & ConfigProps> = ({
  partner,
  config
}) => {
  const translate = useTranslate()
  const [selected, setSelected] = React.useState<RaRecord[]>([])
  const [deleted, setDeleted] = React.useState(false)
  const [allBy, setAllBy] = React.useState<
    { key: string; value: string } | undefined
  >(undefined)
  const { data, isLoading } = useGetIdentity()
  if (isLoading) return <Loading />
  const checkBase = (value: string): string | undefined =>
    !includes('/')(value) ? undefined : 'not use slash'
  return (
    <Box sx={{ ml: 1 }}>
      <Form mode="onBlur" reValidateMode="onBlur">
        <TextInput
          source="base"
          label="resources.spotData.fields.base"
          fullWidth
          defaultValue={
            isNil(process.env['INPUT'])
              ? !isNil(data) && !isNil(data.fullName)
                ? replace(/[ 　]/g, '')(data.fullName)
                : null
              : 'test'
          }
          validate={checkBase}
        />
        <MultipleSpotSelect
          resource="mappings"
          selected={selected}
          setSelected={setSelected}
          deleted={deleted}
          setDeleted={setDeleted}
          config={config}
          noAll
          allBy={config.SpotData.allBy}
          allManagedBy={
            !isNil(data) && !isNil(data.fullName)
              ? replace(/[ 　]/g, '')(data.fullName)
              : null
          }
          setAllBy={setAllBy}
        />
        <>
          {!isEmpty(selected) && (
            <Paper sx={{ ml: 0, mt: 0.5 }}>
              <Box sx={{ p: 0.5 }}>
                <Typography variant="body1">
                  {config.selected ?? translate('resources.spotData2.selected')}
                </Typography>

                {
                  // @ts-expect-error ts(2339)
                  map.convert({ cap: false })((item: RaRecord, index: number) =>
                    size(selected) > 10 ? (
                      <Chip
                        key={`selected-${index}`}
                        label={item['name']}
                        sx={{ mr: '0.5em' }}
                      />
                    ) : (
                      <Chip
                        key={item['name']}
                        onDelete={() => {
                          setDeleted(true)
                          setSelected(
                            filter((record: RaRecord) => record.id !== item.id)(
                              selected
                            )
                          )
                        }}
                        label={item['name']}
                        sx={{ mr: '0.5em' }}
                      />
                    )
                  )(size(selected) > 10 ? slice(0, 10)(selected) : selected)
                }
                {size(selected) > 10 && <Chip label="・・・" />}
              </Box>
            </Paper>
          )}
        </>
        <Box sx={{ mt: 2 }}>
          <SpotDataTable
            selected={selected}
            partner={partner}
            config={config}
            allBy={allBy}
          />
        </Box>
      </Form>
    </Box>
  )
}
